/**
 * 🚀 UNIVERSAL IMAGE ELEMENT 2025
 * 
 * Dynamisches Image-Element für HeroHeader
 * - Voll responsive (Desktop/Mobile)
 * - Upload via Firebase Storage
 * - Individuelle Styling-Optionen pro Element
 * - Border Radius, Shadow, Border Support
 * - Width/Height/Max-Width Controls
 */

import React, { useState, useRef, useEffect } from 'react';
import ImageFloatingToolbar from './ImageFloatingToolbar';
import AnimatedElement from './AnimatedElement';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './UniversalImageElement.css';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

function UniversalImageElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {},
    isCurrentlyEditing = false,
    onEditingStart = () => {},
    onEditingEnd = () => {}
}) {
    const [showToolbar, setShowToolbar] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const imageRef = useRef(null);
    const fileInputRef = useRef(null);

    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte (SOFORTIGES Feedback)
    const [localStyles, setLocalStyles] = useState({
        width: element.width,
        widthMobile: element.widthMobile,
        height: element.height,
        heightMobile: element.heightMobile,
        maxWidth: element.maxWidth,
        maxWidthMobile: element.maxWidthMobile,
        borderRadius: element.borderRadius,
        borderRadiusMobile: element.borderRadiusMobile,
        borderWidth: element.borderWidth,
        shadowIntensity: element.shadowIntensity,
        shadowDistance: element.shadowDistance,
        shadowOpacity: element.shadowOpacity,
        padding: element.padding,
        paddingMobile: element.paddingMobile,
        marginTop: element.marginTop,
        marginBottom: element.marginBottom,
        marginLeft: element.marginLeft,
        marginRight: element.marginRight,
        marginTopMobile: element.marginTopMobile,
        marginBottomMobile: element.marginBottomMobile,
        marginLeftMobile: element.marginLeftMobile,
        marginRightMobile: element.marginRightMobile,
        // Icon Mode
        iconContainerSize: element.iconContainerSize,
        iconContainerSizeMobile: element.iconContainerSizeMobile
    });

    // ⚡ Debounced Parent Update (300ms für Firebase)
    const debouncedParentUpdate = useDebouncedCallback((updates) => {
        if (onUpdate) {
            onUpdate(updates);
        }
    }, 300);

    // ✅ Aktuelle Werte - OPTIMISTIC (localStyles hat Priorität!)
    const currentImageUrl = element.imageUrl || '';
    const currentWidth = isMobileLayout ? 
        (localStyles.widthMobile ?? element.widthMobile ?? localStyles.width ?? element.width ?? 300) : 
        (localStyles.width ?? element.width ?? 400);
    const currentHeight = isMobileLayout ? 
        (localStyles.heightMobile ?? element.heightMobile ?? localStyles.height ?? element.height ?? 200) : 
        (localStyles.height ?? element.height ?? 300);
    const currentMaxWidth = isMobileLayout ?
        (localStyles.maxWidthMobile ?? element.maxWidthMobile ?? localStyles.maxWidth ?? element.maxWidth ?? 600) :
        (localStyles.maxWidth ?? element.maxWidth ?? 800);
    const currentBorderRadius = isMobileLayout ?
        (localStyles.borderRadiusMobile ?? element.borderRadiusMobile ?? localStyles.borderRadius ?? element.borderRadius ?? 8) :
        (localStyles.borderRadius ?? element.borderRadius ?? 8);
    const currentObjectFit = element.objectFit || 'cover';

    // ✅ Image Upload Handler - KONSISTENT mit anderen Komponenten
    const handleImageUpload = async (file) => {
        if (!file) return;
        
        // File Size Check (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            alert('File is too big (max. 2MB).');
            return;
        }
        
        setIsUploading(true);
        
        try {
            // ✅ DIREKTER FIREBASE UPLOAD - EXAKT WIE IN ANDEREN KOMPONENTEN
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            
            if (!auth.currentUser) {
                alert('You must be logged in to upload images.');
                setIsUploading(false);
                return;
            }
            
            const userId = auth.currentUser.uid;
            const timestamp = Date.now();
            const fieldName = `hero_image_${element.id}`;
            const imageRef = storageRef(storage, `pages/${userId}/hero-elements/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log(`📊 Image upload progress: ${progress}%`);
                }, 
                (error) => {
                    console.error('❌ Image upload error:', error);
                    alert('Upload failed. Please try again.');
                    setIsUploading(false);
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        // Update Element mit neuer Image URL
                        if (onUpdate) {
                            onUpdate({ ...element, imageUrl: downloadURL });
                        }
                        setIsUploading(false);
                        console.log('✅ Image uploaded successfully:', downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error('Firebase import error:', error);
            alert('Upload failed. Please try again.');
            setIsUploading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleImageUpload(file);
        }
    };

    const handleClick = (e) => {
        if (!isEditMode) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (isCurrentlyEditing) {
            // ✅ TOGGLE: Schließe Toolbar wenn bereits geöffnet
            handleToolbarClose();
        } else {
            // ✅ Öffne Toolbar (schließt automatisch andere Toolbars via onEditingStart)
            onEditingStart();
            setShowToolbar(true);
            if (onSelect) onSelect();
        }
    };

    const handleToolbarClose = () => {
        setShowToolbar(false);
        onEditingEnd();
    };

    const handleStyleChange = (updates) => {
        console.log('🎨 UniversalImageElement handleStyleChange:', {
            updates,
            element,
            merged: { ...element, ...updates }
        });
        
        // ✅ Liste aller Slider-Keys die optimistisch sein sollen
        const sliderKeys = [
            'width', 'widthMobile',
            'height', 'heightMobile',
            'maxWidth', 'maxWidthMobile',
            'borderRadius', 'borderRadiusMobile',
            'borderWidth',
            'shadowIntensity', 'shadowDistance', 'shadowOpacity',
            'padding', 'paddingMobile',
            'marginTop', 'marginBottom', 'marginLeft', 'marginRight',
            'marginTopMobile', 'marginBottomMobile', 'marginLeftMobile', 'marginRightMobile',
            'iconContainerSize', 'iconContainerSizeMobile'
        ];
        
        const updatedKeys = Object.keys(updates);
        const hasSliderValue = updatedKeys.some(key => sliderKeys.includes(key));
        
        if (hasSliderValue) {
            // ⚡ SLIDER VALUES: Optimistic Local State + Debounced Parent
            const localUpdates = {};
            sliderKeys.forEach(key => {
                if (updates[key] !== undefined) {
                    localUpdates[key] = updates[key];
                }
            });
            
            console.log('⚡ [OPTIMISTIC] Updating local styles:', localUpdates);
            setLocalStyles(prev => ({ ...prev, ...localUpdates }));
            
            // Debounced Parent Update (nach 300ms)
            console.log('💾 [DEBOUNCED] Scheduling Firebase update...');
            debouncedParentUpdate({ ...element, ...updates });
        } else {
            // ✅ NON-SLIDER VALUES (URLs, Colors, Toggles, Presets): Sofort speichern
            console.log('✅ [INSTANT] Non-slider value, saving immediately:', updates);
            if (onUpdate) {
                onUpdate({ ...element, ...updates });
            }
        }
    };

    // ⚡ OPTIMISTIC ELEMENT: Merge element mit localStyles für Toolbar
    // Die Toolbar liest aus diesem merged element und bekommt so die optimistic Werte
    const optimisticElement = {
        ...element,
        ...localStyles  // localStyles überschreiben die element Props
    };

    // ✅ Icon Mode Detection
    const isIconMode = !!(element.iconContainerSize && element.iconContainerSize > 0);
    
    // console.log('🎨 UniversalImageElement Mode:', {
    //     elementId: element.id,
    //     isIconMode,
    //     iconContainerSize: element.iconContainerSize,
    //     containerBgColor: element.containerBgColor
    // });

    // ============================================
    // 🎯 ICON MODE - Komplett separates Rendering
    // ============================================
    if (isIconMode) {
        const iconSize = isMobileLayout 
            ? (localStyles.iconContainerSizeMobile ?? element.iconContainerSizeMobile ?? localStyles.iconContainerSize ?? element.iconContainerSize ?? 40)
            : (localStyles.iconContainerSize ?? element.iconContainerSize ?? 40);
        
        const iconWidth = localStyles.width ?? element.width ?? 20;
        const iconHeight = localStyles.height ?? element.height ?? 20;
        
        const iconContainerStyle = {
            width: appendPx(iconSize),
            height: appendPx(iconSize),
            borderRadius: appendPx(localStyles.borderRadius ?? element.borderRadius ?? 50),
            background: element.containerBgColor || '#667eea',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.3s ease',
            cursor: isEditMode ? 'pointer' : 'default',
            // ✨ NEW: Icon Container Border (separate from image border)
            border: element.iconContainerBorderEnabled 
                ? `${element.iconContainerBorderWidth || 2}px solid ${element.iconContainerBorderColor || '#667eea'}` 
                : 'none',
            boxShadow: element.shadowEnabled ? `0 ${localStyles.shadowDistance ?? element.shadowDistance ?? 4}px ${localStyles.shadowIntensity ?? element.shadowIntensity ?? 10}px rgba(0, 0, 0, ${(localStyles.shadowOpacity ?? element.shadowOpacity ?? 15) / 100})` : 'none'
        };
        
        const iconImageStyle = {
            width: appendPx(iconWidth),
            height: appendPx(iconHeight),
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
            filter: element.invertColors ? 'invert(1)' : 'none'
        };
        
        const outerContainerStyle = {
            width: 'auto',
            display: 'flex',
            justifyContent: element.alignment === 'center' ? 'center' : 
                            element.alignment === 'right' ? 'flex-end' : 'flex-start',
            marginTop: appendPx(isMobileLayout ? (localStyles.marginTopMobile ?? element.marginTopMobile ?? localStyles.marginTop ?? element.marginTop ?? 0) : (localStyles.marginTop ?? element.marginTop ?? 0)),
            marginBottom: appendPx(isMobileLayout ? (localStyles.marginBottomMobile ?? element.marginBottomMobile ?? localStyles.marginBottom ?? element.marginBottom ?? 0) : (localStyles.marginBottom ?? element.marginBottom ?? 0)),
            marginLeft: appendPx(isMobileLayout ? (localStyles.marginLeftMobile ?? element.marginLeftMobile ?? localStyles.marginLeft ?? element.marginLeft ?? 0) : (localStyles.marginLeft ?? element.marginLeft ?? 0)),
            marginRight: appendPx(isMobileLayout ? (localStyles.marginRightMobile ?? element.marginRightMobile ?? localStyles.marginRight ?? element.marginRight ?? 0) : (localStyles.marginRight ?? element.marginRight ?? 0))
        };
        
        return (
            <AnimatedElement
                animationEnabled={element.animationEnabled}
                animationType={element.animationType}
                animationSpeed={element.animationSpeed}
                isEditMode={isEditMode}
                triggerAnimation={element.triggerAnimation}
                isImage={true}
            >
                <div 
                    ref={imageRef}
                    className={`universal-image-container icon-mode ${isEditMode ? 'edit-mode' : ''}`}
                    style={outerContainerStyle}
                    onClick={handleClick}
                >
                {currentImageUrl ? (
                    <div style={iconContainerStyle}>
                        <img
                            src={currentImageUrl}
                            alt={element.altText || 'Icon'}
                            className="universal-icon"
                            style={iconImageStyle}
                        />
                    </div>
                ) : (
                    <div style={{
                        ...iconContainerStyle,
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)'
                    }}>
                        <i className="fas fa-image" style={{ 
                            fontSize: `${iconWidth * 0.5}px`,
                            color: '#a0aec0'
                        }}></i>
                    </div>
                )}
                
                {/* Floating Toolbar - Nur wenn aktiv editiert wird */}
                {isEditMode && showToolbar && isCurrentlyEditing && (
                    <ImageFloatingToolbar
                        element={optimisticElement}  // ⚡ OPTIMISTIC: Merged element mit localStyles
                        onClose={handleToolbarClose}
                        onStyleChange={handleStyleChange}
                        onImageUpload={handleImageUpload}
                        colors={colors}
                        anchorRef={imageRef}
                        isMobileLayout={isMobileLayout}
                        isUploading={isUploading}
                    />
                )}
                
                {/* Upload Loading Overlay */}
                {isUploading && (
                    <div className="universal-image-upload-overlay">
                        <div className="universal-image-spinner"></div>
                        <p>Uploading...</p>
                    </div>
                )}
                </div>
            </AnimatedElement>
        );
    }
    
    // ============================================
    // 🎨 NORMAL MODE - Standard Image Rendering
    // ============================================
    
    // ⚡ Active Padding (OPTIMISTIC) - wird mehrmals gebraucht, daher vorher definieren
    const activePadding = isMobileLayout ? (localStyles.paddingMobile ?? element.paddingMobile ?? localStyles.padding ?? element.padding ?? 0) : (localStyles.padding ?? element.padding ?? 0);
    
    // Normal Image Styles
    const imageStyles = {
        width: element.widthType === 'percent' ? `${currentWidth}%` : appendPx(currentWidth),
        height: element.heightType === 'auto' ? 'auto' : appendPx(currentHeight),
        borderRadius: appendPx(currentBorderRadius),
        objectFit: currentObjectFit,
        objectPosition: 'center',
        display: 'block',
        transition: 'all 0.3s ease',
        filter: element.invertColors ? 'invert(1)' : 'none',
        
        // Max-Width direkt auf Image wenn kein Padding (⚡ OPTIMISTIC)
        ...(!activePadding && {
            maxWidth: appendPx(currentMaxWidth)
        }),
        
        // Border (direkt auf Bild wenn kein Padding) - ⚡ OPTIMISTIC
        ...(element.borderEnabled && !activePadding && {
            border: `${localStyles.borderWidth ?? element.borderWidth ?? 2}px solid ${element.borderColor || '#ddd'}`
        }),
        
        // Shadow (direkt auf Bild wenn kein Padding) - ⚡ OPTIMISTIC
        ...(element.shadowEnabled && !activePadding && {
            boxShadow: `0 ${localStyles.shadowDistance ?? element.shadowDistance ?? 8}px ${localStyles.shadowIntensity ?? element.shadowIntensity ?? 20}px rgba(0, 0, 0, ${(localStyles.shadowOpacity ?? element.shadowOpacity ?? 20) / 100})`
        })
    };
    
    // Container Styles (für Padding Mode)
    const imageContainerStyles = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: appendPx(activePadding),
        borderRadius: appendPx(currentBorderRadius),
        background: (activePadding > 0) ? (element.containerBgColor || '#ffffff') : 'transparent',
        transition: 'all 0.3s ease',
        
        // Border (auf Container wenn Padding vorhanden)
        ...(element.borderEnabled && activePadding > 0 && {
            border: `${localStyles.borderWidth ?? element.borderWidth ?? 2}px solid ${element.borderColor || '#ddd'}`
        }),
        
        // Shadow (auf Container wenn Padding vorhanden)
        ...(element.shadowEnabled && activePadding > 0 && {
            boxShadow: `0 ${localStyles.shadowDistance ?? element.shadowDistance ?? 8}px ${localStyles.shadowIntensity ?? element.shadowIntensity ?? 20}px rgba(0, 0, 0, ${(localStyles.shadowOpacity ?? element.shadowOpacity ?? 20) / 100})`
        })
    };
    
    // Check if this is a small icon
    const isSmallIcon = element.widthType === 'pixel' && currentWidth !== undefined && currentWidth <= 80;
    
    // Outer Container Alignment (⚡ OPTIMISTIC margins)
    const containerStyle = {
        width: isSmallIcon ? 'auto' : '100%',
        display: 'flex',
        justifyContent: element.alignment === 'center' ? 'center' : 
                        element.alignment === 'right' ? 'flex-end' : 'flex-start',
        ...(isSmallIcon && {
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: 'auto'
        }),
        marginTop: appendPx(isMobileLayout ? (localStyles.marginTopMobile ?? element.marginTopMobile ?? localStyles.marginTop ?? element.marginTop ?? 0) : (localStyles.marginTop ?? element.marginTop ?? 0)),
        marginBottom: appendPx(isMobileLayout ? (localStyles.marginBottomMobile ?? element.marginBottomMobile ?? localStyles.marginBottom ?? element.marginBottom ?? 0) : (localStyles.marginBottom ?? element.marginBottom ?? 0)),
        marginLeft: appendPx(isMobileLayout ? (localStyles.marginLeftMobile ?? element.marginLeftMobile ?? localStyles.marginLeft ?? element.marginLeft ?? 0) : (localStyles.marginLeft ?? element.marginLeft ?? 0)),
        marginRight: appendPx(isMobileLayout ? (localStyles.marginRightMobile ?? element.marginRightMobile ?? localStyles.marginRight ?? element.marginRight ?? 0) : (localStyles.marginRight ?? element.marginRight ?? 0))
    };

    return (
        <AnimatedElement
            animationEnabled={element.animationEnabled}
            animationType={element.animationType}
            animationSpeed={element.animationSpeed}
            isEditMode={isEditMode}
            triggerAnimation={element.triggerAnimation}
            isImage={true}
        >
            <div 
                ref={imageRef}
                className={`universal-image-container ${isEditMode ? 'edit-mode' : ''}`}
                style={containerStyle}
                onClick={handleClick}
            >
            {/* Normal Image */}
            {currentImageUrl ? (
                element.padding > 0 ? (
                    // Mit Padding Container
                    <div style={imageContainerStyles}>
                        <img
                            src={currentImageUrl}
                            alt={element.altText || 'Hero Image'}
                            className="universal-image"
                            style={imageStyles}
                            onMouseEnter={(e) => {
                                if (isEditMode && !isCurrentlyEditing) {
                                    e.currentTarget.style.opacity = '0.8';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (isEditMode) {
                                    e.currentTarget.style.opacity = '1';
                                }
                            }}
                        />
                    </div>
                ) : (
                    // Ohne Container
                    <img
                        src={currentImageUrl}
                        alt={element.altText || 'Hero Image'}
                        className="universal-image"
                        style={imageStyles}
                        onMouseEnter={(e) => {
                            if (isEditMode && !isCurrentlyEditing) {
                                e.currentTarget.style.opacity = '0.8';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (isEditMode) {
                                e.currentTarget.style.opacity = '1';
                            }
                        }}
                    />
                )
            ) : (
                <div 
                    className="universal-image-placeholder"
                    style={{
                        ...imageStyles,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)',
                        border: '2px dashed #cbd5e0',
                        cursor: isEditMode ? 'pointer' : 'default'
                    }}
                    onClick={(e) => {
                        if (isEditMode) {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                        }
                    }}
                >
                    <i className="fas fa-image" style={{ fontSize: '48px', color: '#a0aec0', marginBottom: '12px' }}></i>
                    <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>
                        {isEditMode ? 'Click to upload image' : 'No image'}
                    </p>
                </div>
            )}

            {/* Hidden File Input */}
            {isEditMode && (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                />
            )}

            {/* Upload Loading Overlay */}
            {isUploading && (
                <div className="universal-image-upload-overlay">
                    <div className="universal-image-spinner"></div>
                    <p>Uploading...</p>
                </div>
            )}

            {/* 🎨 Floating Toolbar - Nur wenn aktiv editiert wird */}
            {isEditMode && showToolbar && isCurrentlyEditing && (
                <ImageFloatingToolbar
                    element={optimisticElement}  // ⚡ OPTIMISTIC: Merged element mit localStyles
                    onClose={handleToolbarClose}
                    onStyleChange={handleStyleChange}
                    onImageUpload={handleImageUpload}
                    colors={colors}
                    anchorRef={imageRef}
                    isMobileLayout={isMobileLayout}
                    isUploading={isUploading}
                />
            )}
            </div>
        </AnimatedElement>
    );
}

export default UniversalImageElement;

